<template>
  <section class="relative">
    <div class="grid lg:grid-cols-3 grid-cols-1 gap-10 mt-20">

      <div v-for="(plan, index) in availablePlans"
           :key="index"
           :class="[{ 'bg-white': plan.id === currentSubscription.id }]"
           class="max-w-md mx-auto relative flex flex-col rounded-xl shadow-lg">
        <div
            v-if="plansMeta.tagline[plan.type.toLowerCase()] && (currentSubscription.type === 'basic' || plan.id === currentSubscription.id)"
            class="absolute top-0 inset-x-0">
          <div class="flex justify-center">
              <span
                  class="px-2 py-1 -mt-3 text-xs font-medium border border-gray-700 text-white bg-black rounded-md capitalize">
              {{ plan.id === currentSubscription.id ?
                  'current' :
                  plansMeta.tagline[plan.type.toLowerCase()] }}</span>
          </div>
        </div>

        <div class="text-center pt-10">
          <h5 class="text-xl font-semibold">{{ plan.name }}</h5>
          <h2 class="text-5xl mt-8 mb-3 items-center align-middle">
            <sup v-if="plan.id !== 'basic'" class="text-2xl align-middle">{{
                plan.currency === 'eur'
                    ? '&euro;'
                    : '&dollar;'
              }}</sup>{{ plan.id !== 'basic' ? parseInt(plan.price) : 'Free' }}
          </h2>
          <span>
              {{ plan.id !== 'basic'
              ? (billingPeriod !== 'yearly' ? 'per user, per month' : 'per user, per year')
              : '&#8205;' }}
            </span>

        </div>

        <div class="pt-10 px-10 flex items-center justify-center">
          <div class="max-w-sm">
            <p class="text-sm">{{ plansMeta.descriptions[plan.type.toLowerCase()] }}</p>
          </div>
        </div>

        <div class="p-8 flex items-center">
          <ul class="mb-10 m-auto text-left text-sm">
            <li class="my-2">
              <h5 class="text-base font-extrabold dark:text-gray-300">{{ formatNumber(plan.maxPageViews) }}
                Visitors/mo</h5>
            </li>
            <li class="my-2">
              <h5 class="text-base font-extrabold dark:text-gray-300">Max. {{ formatNumber(plan.maxDomains) }}
                {{ plan.maxDomains > 1 ? 'Domains' : 'Domain' }}</h5>
            </li>
            <li class="my-2">
              <h5 class="text-base font-extrabold dark:text-gray-300">
                {{ plan.maxCampaigns === -1 ? 'Unlimited' : 'Max. ' + formatNumber(plan.maxCampaigns) }} Campaigns
              </h5>
            </li>

            <li class="my-2">
              <h5 class="text-base font-normal dark:text-gray-300">
                {{ plansMeta.features['DETAILED_ANALYTICS'] }}
              </h5>
            </li>

            <li class="my-2">
              <h5 class="text-base font-normal dark:text-gray-300">
                {{ plansMeta.features['MULTI_STEP'] }}
                <el-tooltip placement="top">
                  <div slot="content">Capture more user data effortlessly,<br/> avoiding the hassle of long forms.</div>
                <i class="el-icon-question"></i>
              </el-tooltip>
              </h5>
            </li>

            <li v-for="(feature, featureIndex) in plan.features" :key="featureIndex" class="my-2">
              <h5 class="text-base font-normal dark:text-gray-300">
                {{ plansMeta.features[feature] }}
                <el-tooltip v-if="feature === 'A/B_TESTING'" placement="top">
                  <div slot="content">Create multiple variants and split traffic among different designs <br/>
                    to identify which layout and content perform best.</div>
                <i class="el-icon-question"></i>
              </el-tooltip>
              </h5>
            </li>

            <li v-if="!plan.features.includes('A/B_TESTING')" class="my-2">
              <h5 class="text-base font-normal text-gray-500 line-through dark:text-gray-300">
                {{ plansMeta.features['A/B_TESTING'] }}
                <el-tooltip placement="top">
                  <div slot="content">Create multiple variants and split traffic among different designs <br/>
                    to identify which layout and content perform best.</div>
                <i class="el-icon-question"></i>
              </el-tooltip>
              </h5>
            </li>
            <li v-if="!plan.features.includes('NO_WATERMARK')" class="my-2">
              <h5 class="text-base font-normal text-gray-500 line-through dark:text-gray-300">
                {{ plansMeta.features['NO_WATERMARK'] }}
              </h5>
            </li>
            <li v-if="!plan.features.includes('PRIORITY_SUPPORT')" class="my-2">
              <h5 class="text-base font-normal dark:text-gray-300">
                Basic support
              </h5>
            </li>
          </ul>
        </div>

        <div v-if="plan.id !== 'basic'" class="p-5 mt-auto flex justify-center">
          <template v-if="plan.id === currentSubscription.id">
            <button
                v-if="!hasCanceledSubscription"
                class="py-3 px-6 font-medium underline"
                @click.prevent="triggerCancelSubscription">
              Cancel Subscription
            </button>

            <div v-else class="text-center">
              <span class="mb-2 block text-sm">Subscription active until <br>
                {{ $parseDate(currentSubscription.expiresAt) }}.</span>
              <span><button class="underline" @click.prevent="$emit('subscription-renew', plan.id, plan.stripePriceId)">Renew it</button> before it expires.</span>
            </div>
          </template>

          <template v-else-if="!hasCanceledSubscription">
            <button
                class="py-3 px-6 font-medium border rounded-md border-onvocado-secondary
                  transition-all duration-500 text-onvocado-secondary hover:text-white hover:bg-onvocado-secondary"
                :disabled="buttonPlanDisabled(plan)"
                @click.prevent="triggerSetChosenSubscriptionID(plan.id, plan.stripePriceId)">
              {{ buttonPlanText(plan) }}
            </button>
          </template>
        </div>
      </div>
    </div>
    <h5 class="text-center font-medium mt-14">Interested in a custom plan? <a href="#" class="text-onvocado-secondary">Get
      in touch</a></h5>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import plansMixins from '@mixins/plans'

export default {
  name: 'Plans',
  mixins: [plansMixins],
  inject: ['$parseDate'],
  props: {
    availablePlans: {
      type: Array,
      required: true,
    },
    billingPeriod: {
      type: String,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState({
      currentSubscription: (state) => state.users.currentUser.subscription,
    }),
    // formattedMaxCampaigns() {
    //   return this.plan ? this.formatNumber(this.plan.maxCampaigns) : ''
    // },
    // formattedMaxDomains() {
    //   return this.plan ? this.formatNumber(this.plan.maxDomains) : ''
    // },
    // formattedMaxPageViews() {
    //   return this.plan ? this.formatNumber(this.plan.maxPageViews) : ''
    // },
    hasCanceledSubscription() {
      return this.$store.getters['users/hasCanceledSubscription']
    },
  },
  methods: {
    triggerSetChosenSubscriptionID(planID, stripePlanID) {
      this.$emit('subscription-change', planID, stripePlanID)
    },
    triggerCancelSubscription() {
      this.$emit('subscription-cancel')
    },
    formatNumber(value) {
      if (value === -1) {
        return ''
      }
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    buttonPlanText(plan) {
      const currentPlan = this.currentSubscription
      let btnText = ''

      if (currentPlan == null) {
        return ''
      }

      // Capitalize first letter
      const planItemShortName = plan.type.charAt(0).toUpperCase() + plan.type.slice(1)

      if (plan.weight > currentPlan.weight) {
        btnText = 'Upgrade to ' + planItemShortName
      } else {
        btnText = 'Downgrade to ' + planItemShortName
      }

      return btnText
    },
    buttonPlanDisabled(plan) {
      const currentPlan = this.currentSubscription

      return plan.type === currentPlan.type
          ? true
          : currentPlan.type === 'basic'
              ? plan.type === 'basic'
              : false
    },
  },
}
</script>